import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import TreeBasePhoto from "@/components/shared/TreeBasePhoto";
import { NoRealData } from "@/components/shared";
import { api, campaignApi, inspectionApi, type ArborInspectionSummaryApi, type TreeApi, type TreeEvidenceApi } from "@/lib/api";
import PhotoCategorySelect from "@/components/shared/PhotoCategorySelect";
import { clientChecksum, pickOpenInspection, uploadTreePhotos } from "@/lib/treePhotos";
import { isAuthenticated, loginPath } from "@/lib/auth";
import { useFieldGps } from "../useFieldGps";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

function EvidenceThumb({ ev }: { ev: TreeEvidenceApi }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    inspectionApi
      .evidenceFileUrl(ev.inspection_id, ev.evidence_id)
      .then((u) => {
        if (cancelled) {
          if (u) URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ev.inspection_id, ev.evidence_id]);
  return url ? (
    <img src={url} alt={ev.description ?? "Foto do exemplar"} className="aspect-square w-full object-cover rounded-lg" />
  ) : (
    <div className="aspect-square w-full rounded-lg bg-[#E8EAED] flex items-center justify-center">
      <i className="ri-image-line text-2xl text-[#3F4A52]/40" />
    </div>
  );
}

function ColetorTreeContent({ treeId }: { treeId: number }) {
  const navigate = useNavigate();
  const gps = useFieldGps();
  const [tree, setTree] = useState<TreeApi | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [inspections, setInspections] = useState<ArborInspectionSummaryApi[]>([]);
  const [evidence, setEvidence] = useState<TreeEvidenceApi[]>([]);
  const [hasBasePhoto, setHasBasePhoto] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(0);
  const [photoType, setPhotoType] = useState("GENERAL_TREE");
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const baseInput = useRef<HTMLInputElement>(null);
  const extraInput = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    const [ins, ev] = await Promise.all([inspectionApi.listByTree(treeId), inspectionApi.treeEvidence(treeId)]);
    setInspections(ins);
    setEvidence(ev);
  }, [treeId]);

  useEffect(() => {
    document.title = "FloraIQ — Fotos do exemplar";
    api
      .tree(treeId)
      .then(setTree)
      .catch((err) => setLoadError(err instanceof Error ? err.message : "Exemplar não encontrado."));
    reload().catch(() => undefined);
  }, [treeId, reload]);

  // Fotos alem da principal sao evidencias de uma vistoria: usa a vistoria aberta
  // do exemplar ou abre uma de monitoramento, que fica em rascunho.
  const openInspection = pickOpenInspection(inspections);

  const uploadBase = async (file: File) => {
    if (hasBasePhoto && !window.confirm("Substituir a foto principal do exemplar?")) return;
    setBusy("Enviando foto principal...");
    setMessage(null);
    try {
      const checksum = await clientChecksum(file);
      await campaignApi.uploadTreePhoto(treeId, file, checksum ?? undefined);
      setPhotoVersion((v) => v + 1);
      setMessage({ kind: "ok", text: "Foto principal registrada." });
    } catch (err) {
      setMessage({ kind: "error", text: err instanceof Error ? err.message : "Erro ao enviar a foto." });
    } finally {
      setBusy(null);
    }
  };

  const uploadExtras = async (files: File[]) => {
    setMessage(null);
    try {
      const { sent } = await uploadTreePhotos(treeId, files, {
        evidenceType: photoType,
        latitude: gps.latitude,
        longitude: gps.longitude,
        source: "coletor de campo",
        onProgress: setBusy,
      });
      setMessage({ kind: "ok", text: `${sent} ${sent === 1 ? "foto enviada" : "fotos enviadas"}.` });
    } catch (err) {
      setMessage({ kind: "error", text: err instanceof Error ? `Não foi possível enviar: ${err.message}` : "Erro ao enviar." });
    } finally {
      setBusy(null);
      await reload().catch(() => undefined);
    }
  };

  const photos = evidence.filter((e) => e.mime_type?.startsWith("image/") && !e.invalidated_at);

  if (loadError) {
    return (
      <div className="px-4 pt-6 max-w-lg mx-auto space-y-4">
        <NoRealData title="Exemplar não encontrado" description={loadError} icon="ri-leaf-line" />
        <Link to="/coletor/exemplar" className="block text-center text-sm text-[#1B6E4F] font-medium">Buscar outro exemplar</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="sticky top-0 z-10 bg-white border-b border-[#E8EAED] px-4 py-3 flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="w-8 h-8 rounded-lg hover:bg-[#E8EAED] flex items-center justify-center" aria-label="Voltar">
          <i className="ri-arrow-left-line text-[#3F4A52] text-lg" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-heading font-bold text-[#3F4A52] truncate">{tree?.cta_code ?? "Carregando..."}</p>
          <p className="text-xs text-[#3F4A52]/60 truncate">
            {tree ? tree.species_popular_name || tree.species_scientific_name || "Espécie não identificada" : ""}
          </p>
        </div>
        <Link to="/coletor" className="w-8 h-8 rounded-lg hover:bg-[#E8EAED] flex items-center justify-center" aria-label="Início do coletor">
          <i className="ri-home-line text-[#3F4A52] text-lg" />
        </Link>
      </header>

      <main className="px-4 pt-4 pb-24 max-w-lg md:max-w-2xl mx-auto space-y-5">
        {tree && (
          <p className="text-xs text-[#3F4A52]/70">
            {[tree.street, tree.neighborhood].filter(Boolean).join(", ") || "Endereço não informado"}
            {" · "}última vistoria {formatDate(tree.latest_inspection_date)}
          </p>
        )}

        <section className="bg-white border border-[#E8EAED] rounded-xl overflow-hidden">
          <div className="aspect-[4/3] bg-[#E8EAED]">
            <TreeBasePhoto treeId={treeId} alt="Foto principal do exemplar" version={photoVersion} onStatus={setHasBasePhoto} />
          </div>
          <div className="p-3 flex items-center gap-3">
            <div className="flex-1">
              <p className="text-sm font-medium text-[#3F4A52]">Foto principal</p>
              <p className="text-xs text-[#3F4A52]/60">Identifica o exemplar na ficha e no PDF.</p>
            </div>
            <button
              disabled={!!busy}
              onClick={() => baseInput.current?.click()}
              className="px-3 py-2 rounded-lg border border-[#1B6E4F] text-[#1B6E4F] text-xs font-heading font-bold disabled:opacity-50"
            >
              {hasBasePhoto ? "Trocar" : "Registrar"}
            </button>
          </div>
          <input
            ref={baseInput}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) void uploadBase(f);
            }}
          />
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-base font-heading font-bold text-[#3F4A52]">Adicionar fotos</h2>
            <p className="text-xs text-[#3F4A52]/70 mt-1">
              {openInspection
                ? `Vão para a vistoria aberta de ${formatDate(openInspection.inspection_date)}.`
                : "Abre uma vistoria de monitoramento em rascunho e anexa as fotos a ela."}
            </p>
          </div>
          <label className="block text-xs text-[#3F4A52]/80">
            O que a foto mostra
            <PhotoCategorySelect
              value={photoType}
              onChange={setPhotoType}
              className="mt-1 w-full px-3 py-3 rounded-xl border border-[#E8EAED] bg-white text-sm"
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              disabled={!!busy}
              onClick={() => extraInput.current?.click()}
              className="py-4 rounded-xl bg-[#1B6E4F] text-white text-sm font-heading font-bold disabled:opacity-50"
            >
              <i className="ri-camera-line mr-1" /> Tirar foto
            </button>
            <label
              className={`py-4 rounded-xl border border-[#E8EAED] bg-white text-center text-sm font-medium text-[#3F4A52] ${busy ? "opacity-50 pointer-events-none" : "cursor-pointer"}`}
            >
              <i className="ri-image-add-line mr-1" /> Da galeria
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const fs = Array.from(e.target.files ?? []);
                  e.target.value = "";
                  if (fs.length) void uploadExtras(fs);
                }}
              />
            </label>
          </div>
          <input
            ref={extraInput}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const fs = Array.from(e.target.files ?? []);
              e.target.value = "";
              if (fs.length) void uploadExtras(fs);
            }}
          />
          {busy && <p className="text-xs text-[#3F4A52]/70">{busy}</p>}
          {message && (
            <p className={`text-xs ${message.kind === "ok" ? "text-[#1B6E4F]" : "text-[#D32F2F]"}`}>{message.text}</p>
          )}
          {openInspection && (
            <Link
              to={`/cta/${treeId}/inspecoes/${openInspection.id}`}
              className="block text-center text-xs font-medium text-[#0052CC]"
            >
              Completar e enviar a vistoria
            </Link>
          )}
        </section>

        <section>
          <h2 className="text-base font-heading font-bold text-[#3F4A52] mb-2">
            Fotos registradas {photos.length > 0 && <span className="text-[#3F4A52]/50 font-normal">({photos.length})</span>}
          </h2>
          {photos.length === 0 ? (
            <p className="text-sm text-[#3F4A52]/70 bg-white border border-dashed border-[#E8EAED] rounded-xl px-4 py-6 text-center">
              Nenhuma foto de vistoria para este exemplar.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {photos.map((ev) => (
                <div key={ev.evidence_id}>
                  <EvidenceThumb ev={ev} />
                  <p className="text-[10px] text-[#3F4A52]/60 mt-1 truncate">{formatDate(ev.captured_at ?? ev.uploaded_at)}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        <Link
          to={`/cta/${treeId}`}
          className="flex items-center justify-center gap-1 w-full py-4 rounded-xl border border-[#1B6E4F] bg-white text-sm font-medium text-[#1B6E4F] hover:bg-[#1B6E4F]/5"
        >
          <i className="ri-file-list-3-line" /> Abrir ficha completa (FCA)
        </Link>
      </main>
    </div>
  );
}

export default function ColetorTreePage() {
  const { id } = useParams<{ id: string }>();
  const treeId = Number(id);
  if (!isAuthenticated()) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4">
        <Link to={loginPath(`/coletor/exemplar/${id}`)} className="px-5 py-2.5 rounded-xl bg-[#1B6E4F] text-white font-heading font-bold text-sm">
          Entrar
        </Link>
      </div>
    );
  }
  if (!Number.isInteger(treeId) || treeId <= 0) {
    return <NoRealData title="Exemplar inválido" description="Endereço sem identificador de exemplar." icon="ri-leaf-line" variant="page" />;
  }
  return <ColetorTreeContent treeId={treeId} />;
}
